import { Logger } from "./logger.service";
import { LogLevel } from "./log-entry.model";
import { CONSOLE_METHODS, ConsoleMethod, nativeConsole } from "./native-console";
import { toJsonValue } from "./log-value-sanitizer";

let installed = false;
/**
 * Set while a patched `console.*` call is forwarding into `Logger`. Kept only to guard against
 * re-entrancy if some other `console.*` call happens synchronously while forwarding is already in
 * progress — `Logger`'s own console mirroring now always calls `nativeConsole` directly (see
 * `native-console.ts`), so it can no longer loop back into this interceptor.
 */
let isForwarding = false;

/** `console.log` carries no distinct semantic level, so it maps to `INFO` like `console.info`. */
const METHOD_LEVEL: Record<ConsoleMethod, LogLevel> = { log: "INFO", info: "INFO", warn: "WARN", error: "ERROR", debug: "DEBUG" };

/**
 * Monkey-patches `console.log/info/warn/error/debug` exactly once, at startup, so every existing
 * and future `console.*` call site (there are ~35 today) is captured without a migration pass.
 *
 * Each patched method first checks its mapped level against the configured `logLevel`
 * (`Logger.isLevelEnabled()`): a below-level call is dropped entirely — neither printed to DevTools
 * nor persisted. Otherwise it calls the *native* console method (via `nativeConsole`), then routes
 * the message through `Logger`, tagged `"console"`. Remaining arguments are passed through raw as
 * `{ args }` — `Logger` sanitizes every entry's `context` itself.
 *
 * Only `console.log/info/warn/error/debug` are filtered; unpatched methods (`console.group*`,
 * `console.table`, `console.trace`, ...) still print unconditionally.
 *
 * @see Docs/app-logs.md
 */
export function installConsoleInterceptor(logger: Logger): void {
  if (installed) {
    return;
  }
  installed = true;

  for (const method of CONSOLE_METHODS) {
    const level = METHOD_LEVEL[method];
    console[method] = (...args: unknown[]) => {
      if (!logger.isLevelEnabled(level)) {
        return;
      }
      nativeConsole[method](...args);
      if (isForwarding) {
        return;
      }
      isForwarding = true;
      try {
        forwardToLogger(logger, level, args);
      } finally {
        isForwarding = false;
      }
    };
  }
}

function forwardToLogger(logger: Logger, level: LogLevel, args: unknown[]): void {
  const [first, ...rest] = args;
  const message = typeof first === "string" ? first : toMessage(first);
  const context = rest.length > 0 ? { args: rest } : undefined;

  if (level === "WARN") {
    logger.warn("console", message, context);
  } else if (level === "ERROR") {
    logger.error("console", message, context);
  } else if (level === "DEBUG") {
    logger.debug("console", message, context);
  } else {
    logger.info("console", message, context);
  }
}

/** e.g. `console.error(someError)` — reuses `toJsonValue` so it doesn't collapse to `"{}"`. */
function toMessage(value: unknown): string {
  const json = toJsonValue(value);
  return typeof json === "string" ? json : (JSON.stringify(json) ?? String(json));
}
